import type { AnyAction } from '@reduxjs/toolkit';

/**
 * Modal type constants for the global modal system.
 * These constants are used throughout the app to open modals.
 */
export const MODAL_TYPES = {
  // Onboarding modals
  PATIENT_COMMUNICATION_ONBOARDING: 'PATIENT_COMMUNICATION_ONBOARDING',

  // Feature modals
  NEW_FEATURE: 'NEW_FEATURE',
  VERSION_UPDATE: 'VERSION_UPDATE',
  ENABLE_EHR_MODE: 'ENABLE_EHR_MODE',

  // Patient modals
  NEW_PATIENT: 'NEW_PATIENT',
  PATIENT_INFO_COLLECTION: 'PATIENT_INFO_COLLECTION',

  // Payment modals
  STRIPE_CONNECT_INFO: 'STRIPE_CONNECT_INFO',
  // PAYMENT_REQUEST removed — superseded by LEDGER_DRAWER below.

  // Toolbar action modals
  SEND_COMMUNICATION: 'SEND_COMMUNICATION',
  LAB_ORDER: 'LAB_ORDER',
  GENERATE_DOCUMENT: 'GENERATE_DOCUMENT',

  // Document viewer modals
  PDF_VIEWER: 'PDF_VIEWER',
  NOTE_VIEWER: 'NOTE_VIEWER',
  DOCUMENT_VIEWER: 'DOCUMENT_VIEWER',

  FIX_SPELLING: 'FIX_SPELLING',
  JOTDOC_FIND_REPLACE: 'JOTDOC_FIND_REPLACE',
  // Settings modals
  COPY_PASTE_PREFERENCES: 'COPY_PASTE_PREFERENCES',

  // Consent modals
  CONSENT_INFORMATION: 'CONSENT_INFORMATION',

  // Appointment selection modal
  APPOINTMENT_SELECTION: 'APPOINTMENT_SELECTION',

  // Appointment types management modal
  APPOINTMENT_TYPES_MANAGEMENT: 'APPOINTMENT_TYPES_MANAGEMENT',

  // JotCoach feedback modal
  JOTCOACH_FEEDBACK: 'JOTCOACH_FEEDBACK',

  // Approval modal
  APPROVAL_MODAL: 'APPROVAL_MODAL',

  // Notepad modal
  NOTEPAD: 'NOTEPAD',

  // Medication timeline modal
  MEDICATION_TIMELINE: 'MEDICATION_TIMELINE',

  // CSAT modal
  CSAT: 'CSAT',

  // Rule detail modal
  RULE_DETAIL: 'RULE_DETAIL',

  // Rule drilldown modal
  RULE_DRILLDOWN: 'RULE_DRILLDOWN',

  // Entitlement modals
  ENTITLEMENT_CONTACT_ADMIN: 'ENTITLEMENT_CONTACT_ADMIN',

  // Insurance management modal
  INSURANCE_EDIT: 'INSURANCE_EDIT',

  // (Eligibility detail content now lives inside the Patient Ledger Drawer's
  // Eligibility tab — see features/patient-ledger. Trigger via
  // useEligibilityActions().openDrawer.)

  // System notification modal
  SYSTEM_NOTIFICATION: 'SYSTEM_NOTIFICATION',

  // JotAudit video modal
  JOTAUDIT_VIDEO: 'JOTAUDIT_VIDEO',

  // Entitlement upgrade modal (solo practice admin — add-on or tier upgrade)
  ENTITLEMENT_UPGRADE: 'ENTITLEMENT_UPGRADE',

  // The Patient Ledger Drawer (formerly LEDGER_DRAWER) is no longer a
  // modal — it has its own slice + global host. See features/patient-ledger.
  // Trigger via `useOpenLedgerDrawer()`.

  // Addon upsell modal (config-driven education + purchase flow)
  ADDON_UPSELL: 'ADDON_UPSELL',

  // Add-on onboarding modal (config-driven first-run flow for a granted capability)
  ADDON_ONBOARDING: 'ADDON_ONBOARDING',

  // Treatment plan edit modal
  TREATMENT_PLAN_EDIT: 'TREATMENT_PLAN_EDIT',

  // Clinical chart add modals
  ADD_DIAGNOSIS: 'ADD_DIAGNOSIS',
  ADD_MEDICATION: 'ADD_MEDICATION',
  ADD_ALLERGY: 'ADD_ALLERGY',

  // Global command palette (⌘K)
  COMMAND_PALETTE: 'COMMAND_PALETTE',

  // Add new modal types here...
} as const;

/**
 * TypeScript type derived from the modal types object.
 * This ensures type safety when using modal types.
 */
export type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES];

/**
 * Continuation action interface for post-modal callbacks.
 * Allows modals to dispatch Redux actions after completion.
 */
export interface ContinuationAction extends AnyAction {
  type: string;
  payload?: any;
  meta?: any;
}

/**
 * Standard props interface for all modal components.
 * All modals in the system should accept these props.
 */
export interface ModalComponentProps {
  modalProps: Record<string, any> & {
    continuationAction?: ContinuationAction;
  };
  closeModal: () => void;
}
